const AUTO_SCROLL_EDGE      = 48;
const AUTO_SCROLL_MAX_SPEED = 16;

let sortable = {};

const getPointer = (event) => {
    const touch = event.touches?.[0] ?? event.changedTouches?.[0];

    return touch ?? event;
};

const getMouseY = (event) => {
    const clientY = getPointer(event).clientY;
    const offset  = clientY - sortable.container.element.getBoundingClientRect().top;

    return offset < 0 ? 0 : offset;
};

const findScroller = (element) => {
    for (let node = element; node && node !== document.body; node = node.parentElement) {
        const overflowY = getComputedStyle(node).overflowY;

        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
    }

    return null;
};

const autoScrollSpeed = () => {
    const rect   = sortable.scroller.getBoundingClientRect();
    const top    = Math.max(rect.top, 0);
    const bottom = Math.min(rect.bottom, window.innerHeight);
    const y      = sortable.pointer.lastY;

    if (y < top + AUTO_SCROLL_EDGE) {
        return -Math.ceil(AUTO_SCROLL_MAX_SPEED * Math.min(1, (top + AUTO_SCROLL_EDGE - y) / AUTO_SCROLL_EDGE));
    }

    if (y > bottom - AUTO_SCROLL_EDGE) {
        return Math.ceil(AUTO_SCROLL_MAX_SPEED * Math.min(1, (y - (bottom - AUTO_SCROLL_EDGE)) / AUTO_SCROLL_EDGE));
    }

    return 0;
};

const autoScroll = () => {
    const scroller        = sortable.scroller;
    const scrollTopBefore = scroller.scrollTop;

    scroller.scrollTop += autoScrollSpeed();

    if (scroller.scrollTop === scrollTopBefore) {
        sortable.autoScrollFrame = null;

        return;
    }

    sortable.autoScrollFrame = requestAnimationFrame(autoScroll);
};

const startAutoScroll = () => {
    if (!sortable.scroller || sortable.autoScrollFrame) {
        return;
    }

    sortable.autoScrollFrame = requestAnimationFrame(autoScroll);
};

const stopAutoScroll = () => {
    if (!sortable.scroller) {
        return;
    }

    cancelAnimationFrame(sortable.autoScrollFrame);
    sortable.autoScrollFrame = null;
    sortable.scroller.removeEventListener('scroll', handleScroll);
};

const startSort = (event) => {
    if (event.button === 2) {
        return;
    }

    const dragHandle = event.target.closest('[data-sortable-handle]');

    if (!dragHandle) {
        return;
    }

    const pointer   = getPointer(event);
    const target    = dragHandle.closest(dragHandle.dataset.sortableTarget);
    const container = target.parentNode;

    const crossContainerSelector  = dragHandle.dataset.sortableContainers || null;
    const ghostContainerSelector  = dragHandle.dataset.sortableGhostContainer || null;
    const dropTargetSelector      = dragHandle.dataset.sortableDropTargets || null;
    const dropOnly                = 'sortableDropOnly' in dragHandle.dataset;
    const axis                    = dragHandle.dataset.sortableAxis ?? 'y';
    const allContainers           = crossContainerSelector
        ? [...document.querySelectorAll(crossContainerSelector)]
        : [container];

    container.classList.add('rearranging');

    const startNextSibling = target.nextElementSibling;

    const ghost = target.cloneNode(true);

    ghost.classList.add('ghost');

    ghost.style.top   = `${target.offsetTop}px`;
    ghost.style.left  = `${target.offsetLeft}px`;
    ghost.style.width = `${target.offsetWidth}px`;

    if (!dropOnly) {
        target.classList.add('placeholder');
    }

    target.after(ghost);

    const ghostContainer = ghostContainerSelector
        ? target.closest(ghostContainerSelector)
        : null;

    const ghostRect = ghost.getBoundingClientRect();

    if (ghostContainer) {
        const containerRect = ghostContainer.getBoundingClientRect();

        ghostContainer.appendChild(ghost);

        ghost.style.position = 'absolute';
        ghost.style.zIndex   = '30000';
        ghost.style.top      = `${ghostRect.top - containerRect.top + ghostContainer.scrollTop}px`;
        ghost.style.left     = `${ghostRect.left - containerRect.left + ghostContainer.scrollLeft}px`;
    } else if (crossContainerSelector) {
        ghost.style.position = 'fixed';
        ghost.style.zIndex   = '30000';
        ghost.style.top      = `${ghostRect.top}px`;
        ghost.style.left     = `${ghostRect.left}px`;
    } else if (dropTargetSelector) {
        ghost.style.position      = 'fixed';
        ghost.style.zIndex        = '30000';
        ghost.style.top           = `${ghostRect.top}px`;
        ghost.style.left          = `${ghostRect.left}px`;
        ghost.style.pointerEvents = 'none';
    }

    sortable = {
        container: {
            element: container,
        },
        scroller: axis === 'x' ? null : findScroller(container),
        autoScrollFrame: null,
        allContainers: allContainers,
        crossContainerSelector: crossContainerSelector,
        ghostContainer: ghostContainer,
        dropTargetSelector: dropTargetSelector,
        dropOnly: dropOnly,
        axis: axis,
        activeDropTarget: null,
        target: {
            element: target,
            selector: dragHandle.dataset.sortableTarget,
            startContainer: container,
            startNextSibling: startNextSibling,
            restore: restoreToStartPosition(target, container, startNextSibling),
            startPosition: {
                index: [...container.children].indexOf(target),
                top: target.offsetTop,
                left: target.offsetLeft,
                scrollTop: container.scrollTop,
            },
        },
        pointer: {},
        ghost: {
            element: ghost,
            startViewportTop: ghostRect.top,
        },
    };

    sortable.pointer = {
        startY: (crossContainerSelector || dropTargetSelector) ? pointer.clientY : getMouseY(event),
        startX: pointer.clientX,
        startGhostTop: ((crossContainerSelector || dropTargetSelector) && !ghostContainer) ? ghostRect.top : 0,
        startGhostLeft: ghostRect.left,
        lastX: pointer.clientX,
        lastY: pointer.clientY,
    };

    sortable.scroller?.addEventListener('scroll', handleScroll);

    dispatchEvent('sortablestart');

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleDrop);
    document.addEventListener('contextmenu', handleCancel);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleDrop);

    document.removeEventListener('mousedown', startSort);
    document.removeEventListener('touchstart', startSort);
};

const moveGhost = (event) => {
    const ghost = sortable.ghost.element;

    if (sortable.ghostContainer) {
        const ghostContainer = sortable.ghostContainer;
        const deltaY         = getPointer(event).clientY - sortable.pointer.startY;
        const containerTop   = ghostContainer.getBoundingClientRect().top;
        const top            = sortable.ghost.startViewportTop + deltaY - containerTop + ghostContainer.scrollTop;

        ghost.style.top = `${Math.max(0, Math.min(top, ghostContainer.scrollHeight - ghost.offsetHeight))}px`;
    } else if (sortable.dropTargetSelector) {
        const pointer = getPointer(event);
        const left    = sortable.dropOnly
            ? sortable.pointer.startGhostLeft
            : sortable.pointer.startGhostLeft + (pointer.clientX - sortable.pointer.startX);
        const top     = sortable.pointer.startGhostTop + (pointer.clientY - sortable.pointer.startY);

        ghost.style.left = `${Math.max(0, Math.min(left, window.innerWidth - ghost.offsetWidth))}px`;
        ghost.style.top  = `${Math.max(0, Math.min(top, window.innerHeight - ghost.offsetHeight))}px`;
    } else if (sortable.crossContainerSelector) {
        const deltaY = getPointer(event).clientY - sortable.pointer.startY;
        const top    = sortable.pointer.startGhostTop + deltaY;

        const bounds = sortable.allContainers.reduce((union, container) => {
            const rect = container.getBoundingClientRect();

            return {
                top:    Math.min(union.top, rect.top),
                bottom: Math.max(union.bottom, rect.bottom),
            };
        }, { top: Infinity, bottom: -Infinity });

        ghost.style.top = `${Math.max(bounds.top, Math.min(top, bounds.bottom - ghost.offsetHeight))}px`;
    } else if (sortable.axis === 'x') {
        const container = sortable.container.element;
        const left      = sortable.target.startPosition.left + (getPointer(event).clientX - sortable.pointer.startX);

        ghost.style.left = `${Math.max(0, Math.min(left, container.scrollWidth - ghost.offsetWidth))}px`;
    } else {
        const container   = sortable.container.element;
        const scrollDelta = container.scrollTop - sortable.target.startPosition.scrollTop;
        const top         = sortable.target.startPosition.top + (getMouseY(event) - sortable.pointer.startY) + scrollDelta;

        ghost.style.top = `${Math.max(0, Math.min(top, container.scrollHeight - ghost.offsetHeight))}px`;
    }
};

const movePlaceholderInContainer = (container, mouseY, mouseX) => {
    const items = container.querySelectorAll(`:scope > ${sortable.target.selector}:not(.ghost)`);

    for (let i = 0; i < items.length; i++) {
        const rect            = items[i].getBoundingClientRect();
        const isPointerBefore = sortable.axis === 'x'
            ? mouseX < rect.left + (rect.width / 2)
            : mouseY < rect.top + (rect.height / 2);

        if (isPointerBefore) {
            items[i].before(sortable.target.element);

            return;
        }
    }

    if (items.length > 0) {
        items[items.length - 1].after(sortable.target.element);
    } else {
        container.appendChild(sortable.target.element);
    }
};

const updateDropTarget = (event) => {
    if (!sortable.dropTargetSelector) {
        return;
    }

    const pointer    = getPointer(event);
    const dropTarget = document.elementFromPoint(pointer.clientX, pointer.clientY)
        ?.closest(sortable.dropTargetSelector) ?? null;

    if (dropTarget === sortable.activeDropTarget) {
        return;
    }

    sortable.activeDropTarget?.classList.remove('drop-hover');
    dropTarget?.classList.add('drop-hover');

    sortable.activeDropTarget = dropTarget;
};

const movePlaceholder = (event) => {
    if (sortable.dropOnly) {
        return;
    }

    const mouseY = getPointer(event).clientY;

    if (sortable.dropTargetSelector) {
        if (sortable.activeDropTarget) {
            return;
        }

        const mouseX        = getPointer(event).clientX;
        const containerRect = sortable.container.element.getBoundingClientRect();

        if (mouseX < containerRect.left || mouseX > containerRect.right) {
            return;
        }
    }

    if (sortable.crossContainerSelector) {
        let closestContainer = sortable.allContainers[0];
        let closestDistance  = Infinity;

        for (const container of sortable.allContainers) {
            const rect     = container.getBoundingClientRect();
            const distance = Math.max(rect.top - mouseY, 0, mouseY - rect.bottom);

            if (distance < closestDistance) {
                closestDistance  = distance;
                closestContainer = container;
            }
        }

        if (sortable.container.element !== closestContainer) {
            sortable.container.element.classList.remove('rearranging');
            sortable.container.element = closestContainer;
            closestContainer.classList.add('rearranging');
        }
    }

    movePlaceholderInContainer(sortable.container.element, mouseY, getPointer(event).clientX);
};

const readPosition = element => (sortable.axis === 'x' ? element.offsetLeft : element.offsetTop);

const swallowClick = event => event.stopPropagation();

const followPointer = (pointer) => {
    moveGhost(pointer);
    updateDropTarget(pointer);

    const positionBeforeMove  = readPosition(sortable.target.element);
    const containerBeforeMove = sortable.target.element.parentNode;

    movePlaceholder(pointer);

    if (positionBeforeMove !== readPosition(sortable.target.element) || containerBeforeMove !== sortable.target.element.parentNode) {
        dispatchEvent('sortablemove');
    }
};

const handleMove = (event) => {
    event.preventDefault();

    const pointer = getPointer(event);

    sortable.pointer.lastX = pointer.clientX;
    sortable.pointer.lastY = pointer.clientY;

    followPointer(event);
    startAutoScroll();
};

const handleScroll = () => followPointer({clientX: sortable.pointer.lastX, clientY: sortable.pointer.lastY});

const handleDrop = () => {
    stopAutoScroll();

    document.removeEventListener('mousemove', handleMove);
    document.removeEventListener('mouseup', handleDrop);
    document.removeEventListener('touchmove', handleMove);
    document.removeEventListener('touchend', handleDrop);

    const dropTarget = sortable.activeDropTarget;

    if (dropTarget) {
        dropTarget.classList.remove('drop-hover');
        sortable.target.restore();
    }

    // a drop-only handle only drops; releasing it must never click what it sits in
    if (sortable.dropOnly) {
        document.addEventListener('click', swallowClick, {capture: true});
    }

    // required to defer the removal of the contextmenu listener to the next tick
    // as the contextmenu is triggered after the mouseup event
    setTimeout(() => {
        document.removeEventListener('contextmenu', handleCancel);
        document.removeEventListener('click', swallowClick, {capture: true});

        if (dropTarget) {
            dispatchEvent('sortabledroptarget', {dropTarget});
        } else {
            dispatchEvent('sortabledrop');
        }
    }, 0);

    sortable.allContainers.forEach(c => c.classList.remove('rearranging'));

    sortable.ghost.element.parentNode.removeChild(sortable.ghost.element);

    sortable.target.element.classList.remove('placeholder');

    document.addEventListener('mousedown', startSort);
    document.addEventListener('touchstart', startSort, { passive: false });
};

const handleCancel = (event) => {
    event.preventDefault();

    stopAutoScroll();

    document.removeEventListener('mousemove', handleMove);
    document.removeEventListener('mouseup', handleDrop);
    document.removeEventListener('contextmenu', handleCancel);
    document.removeEventListener('touchmove', handleMove);
    document.removeEventListener('touchend', handleDrop);

    sortable.activeDropTarget?.classList.remove('drop-hover');
    sortable.activeDropTarget = null;

    sortable.target.restore();

    sortable.allContainers.forEach(c => c.classList.remove('rearranging'));
    sortable.ghost.element.parentNode.removeChild(sortable.ghost.element);
    sortable.target.element.classList.remove('placeholder');

    dispatchEvent('sortablecancel');

    document.addEventListener('mousedown', startSort);
    document.addEventListener('touchstart', startSort, { passive: false });
};

const restoreToStartPosition = (element, startContainer, startNextSibling) => () => {
    if (startNextSibling && startNextSibling.parentNode === startContainer) {
        startNextSibling.before(element);

        return;
    }

    startContainer.appendChild(element);
};

const dispatchEvent = (eventName, extraDetail = {}) => {
    sortable.target.element.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        detail: {
            ghost: sortable.ghost.element,
            sourceIndex: sortable.target.startPosition.index,
            destinationIndex: [...sortable.container.element.children].indexOf(sortable.target.element),
            sourceContainer: sortable.target.startContainer,
            destinationContainer: sortable.container.element,
            restore: sortable.target.restore,
            ...extraDetail,
        },
    }));
};

document.addEventListener('mousedown', startSort);
document.addEventListener('touchstart', startSort, { passive: false });
